import { Stack, TextField } from '@mui/material';
import { VySwitch } from '../../../../../../components/new-design/inputs/VySwitchNew';
import { isNotNullOrUndefined } from '@vy/utils';
import { get } from 'lodash';
import React from 'react';
import { Controller, Control, UseFormRegister, FieldErrors } from 'react-hook-form';
import { useActivatedIntegration } from '../../../../../../hooks/use-activated-integration';
import { INTEGRATION_TYPES } from '@vy/integrations-types';
import { NetSuiteProductsDropdown } from '../../../../../plans/CreatePlanPage/create-plan-page-components/FixedChargeEditDrawer/fixed-charge-edit-drawer-components/components/NetSuiteProductsDropdown';
import { CatalogProductsFormData } from '../create-product-dialog-types';
import { AnrokProductInput, NetSuiteClassDropdownForCatalog, PaddleProductsDropdownForCatalog, QuickBooksProductsDropdownForCatalog, XeroProductsDropdownForCatalog } from './components';
import { CreditTypesMultiSelect } from '../../../../shared/CreditTypesMultiSelect';

type UpsertCatalogProductsBodyProps = {
  control: Control<CatalogProductsFormData>;
  register: UseFormRegister<CatalogProductsFormData>;
  errors: FieldErrors<CatalogProductsFormData>;
};

export const UpsertCatalogProductsBody: React.FC<UpsertCatalogProductsBodyProps> = ({
  control,
  register,
  errors,
}) => {
  const isNetSuiteConnected = useActivatedIntegration(INTEGRATION_TYPES.NETSUITE);
  const isQuickBooksConnected = useActivatedIntegration(INTEGRATION_TYPES.QUICKBOOKS);
  const isXeroConnected = useActivatedIntegration(INTEGRATION_TYPES.XERO);
  const isAnrokConnected = useActivatedIntegration(INTEGRATION_TYPES.ANROK);
  const isPaddleConnected = useActivatedIntegration(INTEGRATION_TYPES.PADDLE);

  const nameRegister = register('name');
  const descriptionRegister = register('description');

  return (
    <Stack spacing={2}>
      <TextField
        label="Product name"
        placeholder="Enter product name"
        fullWidth
        size="small"
        {...nameRegister}
        error={isNotNullOrUndefined(get(errors, nameRegister.name))}
        helperText={get(errors, nameRegister.name)?.message}
      />

      <TextField
        label="Description (Optional)"
        placeholder="Enter a description of what this product includes"
        fullWidth
        size="small"
        {...descriptionRegister}
      />

      <Controller
        control={control}
        name="isTaxable"
        defaultValue={true}
        render={({ field }) => (
          <VySwitch
            checked={field.value ?? true}
            onChange={(_, checked) => field.onChange(checked)}
            label="Tax on this product"
          />
        )}
      />

      <Controller
        control={control}
        name="creditTypeIds"
        defaultValue={[]}
        render={({ field }) => (
          <CreditTypesMultiSelect
            value={field.value ?? []}
            onChange={field.onChange}
            dataVy="upsert-catalog-product-credit-types-select"
          />
        )}
      />

      {isNetSuiteConnected && (
        <>
          <Controller
            control={control}
            name="netSuiteProduct"
            render={() => (
              <NetSuiteProductsDropdown
                control={control}
                fieldPath="netSuiteProduct"
                showDropdown={true}
                placeholder="Select NetSuite product"
              />
            )}
          />
          <NetSuiteClassDropdownForCatalog control={control} />
        </>
      )}

      {isQuickBooksConnected && <QuickBooksProductsDropdownForCatalog control={control} />}

      {isXeroConnected && <XeroProductsDropdownForCatalog control={control} />}

      {isAnrokConnected && <AnrokProductInput control={control} />}

      {isPaddleConnected && <PaddleProductsDropdownForCatalog control={control} />}
    </Stack>
  );
};
